import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Skeleton } from "@/components/Skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Lock, ShieldCheck, AlertTriangle,
} from "lucide-react";
import MessageRow from "@/components/connect/MessageRow";
import ChatComposer from "@/components/connect/ChatComposer";

const POLL_INTERVAL_MS = 3000;

export default function ConnectionChat() {
  const { connectId } = useParams();
  const navigate = useNavigate();

  const [conn, setConn] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const lastSeenTsRef = useRef("");
  const initializedRef = useRef(false);

  // Initial load + recurring polling
  useEffect(() => {
    let alive = true;

    const initialLoad = async () => {
      try {
        const [{ data: c }, { data: msgs }] = await Promise.all([
          api.get(`/connect/${connectId}`),
          api.get(`/connect/${connectId}/messages`),
        ]);
        if (!alive) return;
        setConn(c);
        setMessages(msgs);
        if (msgs.length) lastSeenTsRef.current = msgs[msgs.length - 1].sent_at;
        api.post(`/connect/${connectId}/read`).catch(() => {});
        initializedRef.current = true;
      } catch (e) {
        if (e?.response?.status === 404) { setNotFound(true); return; }
        toast.error("Could not load conversation.");
      } finally {
        if (alive) setLoadingInitial(false);
      }
    };

    const pollNew = async () => {
      if (!initializedRef.current) return;
      try {
        const after = lastSeenTsRef.current;
        const { data } = await api.get(`/connect/${connectId}/messages`, {
          params: after ? { after_ts: after } : {},
          timeout: 8000,
        });
        if (!alive) return;
        if (data.length > 0) {
          setMessages((prev) => [...prev, ...data]);
          lastSeenTsRef.current = data[data.length - 1].sent_at;
          api.post(`/connect/${connectId}/read`).catch(() => {});
        }
      } catch {
        // transient — silently retry on next tick
      }
    };

    initialLoad();
    const pollTimer = setInterval(pollNew, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(pollTimer); };
  }, [connectId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const isExpired = conn?.is_expired || conn?.status !== "active";
  const canSend = !isExpired && !sending && !uploading;

  const sendText = async (body) => {
    if (!body || !canSend) return false;
    setSending(true);
    try {
      const { data } = await api.post(`/connect/${connectId}/message`, { body });
      setMessages((prev) => [...prev, data]);
      lastSeenTsRef.current = data.sent_at;
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send message");
      return false;
    } finally { setSending(false); }
  };

  const uploadFile = async (file, kind = "file", caption = "") => {
    if (!file || !canSend) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    if (caption) form.append("caption", caption);
    try {
      const { data } = await api.post(`/connect/${connectId}/attachment`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setMessages((prev) => [...prev, data]);
      lastSeenTsRef.current = data.sent_at;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally { setUploading(false); }
  };

  if (notFound) {
    return (
      <Layout title="Conversation">
        <div className="max-w-md mx-auto text-center py-20" data-testid="chat-not-found">
          <p className="text-sm text-muted-foreground">This conversation isn&apos;t available.</p>
          <Link to="/inbox"><Button className="rounded-full mt-4">Back to inbox</Button></Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={conn?.counterpart?.display_name || "Conversation"}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => navigate("/inbox")}
          className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted transition shrink-0"
          aria-label="Back to inbox"
          data-testid="chat-back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        {loadingInitial || !conn ? (
          <Skeleton className="h-12 flex-1" />
        ) : (
          <>
            {conn.counterpart?.photo_url ? (
              <img src={conn.counterpart.photo_url} alt="" className="h-10 w-10 rounded-full object-cover" />
            ) : (
              <span className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-400 to-amber-300 grid place-items-center text-white font-medium">
                {(conn.counterpart?.display_name || "?").charAt(0).toUpperCase()}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-medium leading-tight truncate" data-testid="chat-counterpart-name">
                {conn.counterpart?.display_name || "Educator"}
              </p>
              <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                <Lock className="h-3 w-3" />
                Private · {conn.is_expired ? "expired (read-only)" : `${conn.days_left} day${conn.days_left === 1 ? "" : "s"} left`}
              </p>
            </div>
            {conn.counterpart?.username && (
              <Link
                to={`/u/${conn.counterpart.username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline"
                data-testid="chat-counterpart-profile"
              >
                View profile
              </Link>
            )}
          </>
        )}
      </div>

      {/* Expired banner */}
      {!loadingInitial && conn?.is_expired && (
        <div
          className="mb-3 rounded-xl border border-amber-200/60 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 p-3 text-xs flex items-start gap-2"
          data-testid="chat-expired-banner"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900 dark:text-amber-200">This conversation has expired.</p>
            <p className="text-amber-800/80 dark:text-amber-200/80 mt-0.5">
              You can still read past messages. Re-open Secure Connect from the educator&apos;s profile to chat again.
            </p>
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className="rounded-2xl border border-border bg-card/50 p-3 sm:p-4 min-h-[420px] max-h-[60vh] overflow-y-auto space-y-3 tb-no-scrollbar"
        data-testid="chat-messages"
      >
        {loadingInitial ? (
          <>
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="h-12 w-1/2 ml-auto" />
            <Skeleton className="h-12 w-3/4" />
          </>
        ) : messages.length === 0 ? (
          <div className="grid place-items-center text-center py-10 text-xs text-muted-foreground">
            <span className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-muted mb-3">
              <Lock className="h-5 w-5" />
            </span>
            Say hi — this conversation is private and only you two can read it.
          </div>
        ) : (
          messages.map((m) => <MessageRow key={m.message_id} m={m} />)
        )}
      </div>

      {/* Composer */}
      <div className="mt-3" data-testid="chat-composer">
        <ChatComposer
          canSend={canSend}
          isExpired={isExpired}
          sending={sending}
          uploading={uploading}
          onSendText={sendText}
          onUploadFile={uploadFile}
        />
      </div>

      {/* Privacy footer */}
      <p className="mt-3 text-[10px] text-muted-foreground text-center inline-flex items-center gap-1.5 justify-center w-full">
        <ShieldCheck className="h-3 w-3" /> Private · only the two of you can read this · auto-expires {conn ? new Date(conn.expires_at).toLocaleDateString() : ""}
      </p>
    </Layout>
  );
}
